<template>
  <d-field>
    <template v-for="name in ['before', 'after', 'label', 'help', 'errors']">
      <slot :slot="name" :name="name" />
    </template>

    <template v-for="name in ['prefixIcon', 'suffixIcon', 'prefixLabel', 'suffixLabel']">
      <slot :slot="name" :name="name" />
    </template>

    <slot slot="default" name="input" :value="inputValue">
      <textarea ref="codemirror" />
    </slot>
  </d-field>
</template>

<script>
  /**
   * CodeMirror is a wonderful utility for highlighting code
   * This component provides a way to render a piece of code in a human readable way.
   * By default, the code will be editable by the user; to make it a read-only, use the `readOnly` config option
   */
  export default {
    model: {
      prop: 'value',
      event: 'input',
    },

    props: {
      /**
       * CodeMirror Config Options
       * See the [docs](https://codemirror.net/doc/manual.html#config)
       */
      options: {
        type: Object,
        default() {
          return {
            tabSize: 2,
            lineNumbers: true,
            lineWrapping: false,
            line: true,
          };
        },
      },
      /**
       * CodeMirror modes
       * See the [docs](https://codemirror.net/mode/index.html)
       */
      modes: {
        type: Array,
        default() {
          return ['htmlmixed'];
        },
      },

      /**
         * Input value
         * @model
         */
      value: String,
      /**
       * Input name
       */
      name: {
        type: String,
      },
      /**
       * Color modifier
       */
      color: {
        type: String,
      },
      /**
       * Make input required
       * Required validation rule will be automatically applied, if true
       */
      required: {
        type: Boolean,
        default: false,
      },
      /**
       * Accessibility ID of the field/input
       */
      id: {
        type: String,
        default() {
          const hash = Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, '')
            .substr(0, 5);

          return `n-field-vue-${hash}`;
        },
      },
      /**
       * Field label
       */
      label: {
        type: String,
      },
      /**
       * Field help text
       */
      help: {
        type: String,
      },
      /**
       * Placeholder text
       */
      placeholder: {
        type: String,
      },
      /**
       * Sets loading state
       */
      loading: {
        type: Boolean,
        default: false,
      },
      /**
       * Input type
       */
      type: {
        type: String,
      },
      /**
       * Prefix icon class of the input
       */
      prefixIcon: {
        type: String,
      },
      /**
       * Suffix icon class of the input
       */
      suffixIcon: {
        type: String,
      },
      /**
       * An array of validation rules
       */
      rules: {
        type: Array,
        default: () => [],
      },
      validationEvents: {
        type: Array,
        default: () => ['change'],
      },
    },
    data() {
      const validationRules = this.rules || [];

      return {
        inputValue: typeof this.value === 'undefined' ? null : this.value,
        lastInputValue: null,
        error: null,
        shouldValidate: true,
        validationRules,
        awaiting: false,
        localConfig: {
          name: 'NCodemirror',
          config: {
            baseClass: 'n-codemirror',
          },
        },
        instance: null,
      };
    },
    watch: {
      value() {
        this.setValue(this.value);
      },
    },
    mounted() {
      this.$nextTick(() => {
        this.$codemirror.load({
          modes: this.modes,
        }).then((CodeMirror) => {
          this.instance = CodeMirror.fromTextArea(this.$refs.codemirror, this.options);
          this.instance.setValue(this.value);
          this.instance.on('change', () => {
            this.inputValue = this.instance.getValue();
          });
        });
      });
    },
    methods: {
      setValue(value) {
        if (this.instance) {
          this.instance.setValue(value);
          this.refresh();
        }
      },
      refresh() {
        this.$nextTick(() => {
          this.instance.refresh();
        });
      },
    },
  };
</script>